import { useTranslation } from 'react-i18next';

import { createElement } from 'react';

import BalanceCell from '../components/BalanceCell/BalanceCell';
import { TotalsLabel } from '../EmploymentHours.styles';
import { formatHours, formatPeriodMonth } from '../EmploymentHours.utils';
import { gridColumnSpecs, HOURS_GROUPS } from './columnSpec';
import { matchesClaFlags } from './columnSpec.types';
import { resolveHeaderLabel, resolveTooltip } from './resolveHeaderLabel';

import type { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-pro';
import type { EmploymentHoursGridRow } from '../EmploymentHours.types';
import type { HoursClaFlag, HoursColumnSpec } from './columnSpec.types';

const DEFAULT_COLUMN_WIDTH = 82;

type UseEmploymentHoursColumnsParams = {
  /** `claYear.flags`; only PLB_VISIBILITY and PLB_10_YEARS affect the column set */
  flags?: string[];
  plbName?: string;
  selectedYear?: number;
  /** org preference `hour.management.presentation.mode` */
  showPlusMinus: boolean;
};

export type EmploymentHoursColumns = {
  columns: GridColDef<EmploymentHoursGridRow>[];
  columnGroupingModel: GridColumnGroupingModel;
  /** every field the current flags allow — used to sanitise a stale visibility preference */
  availableFields: string[];
};

/**
 * Sign-flipping columns: Dojo's `plusMinusFormatter` ones plus the two balance columns, whose cells
 * flip inside BalanceCell. Including the balance columns here keeps the CSV export consistent with
 * what is on screen.
 */
export const flipsSign = (spec: HoursColumnSpec) =>
  Boolean(spec.signFlipWhenPlusMinus) ||
  spec.render === 'balance' ||
  spec.render === 'cumulativeBalance';

/**
 * Turns the declarative column spec into DataGrid columns plus a grouping model.
 *
 * Replaces `EmployeeWorkAgreementsGridColumns.js` (909 lines) — everything that file expressed as
 * repeated object literals is data here, and the three behaviours that genuinely differ per column
 * (period rendering, balance rendering, sign flipping) are the only branches.
 */
export const useEmploymentHoursColumns = ({
  flags,
  plbName,
  selectedYear,
  showPlusMinus
}: UseEmploymentHoursColumnsParams): EmploymentHoursColumns => {
  'use memo';

  const { t } = useTranslation('common');

  const claFlags = (flags ?? []) as HoursClaFlag[];
  const specs = gridColumnSpecs().filter((spec) =>
    matchesClaFlags(spec, claFlags)
  );
  const labelContext = { t, plbName, selectedYear };

  const groupLabelOf = (spec: HoursColumnSpec) => {
    const group = HOURS_GROUPS.find((candidate) => candidate.id === spec.group);

    return group ? resolveHeaderLabel(group.label, labelContext) : '';
  };

  const columns = specs.map<GridColDef<EmploymentHoursGridRow>>((spec) => {
    const shouldFlip = showPlusMinus && flipsSign(spec);
    const label = resolveHeaderLabel(spec.label, labelContext);
    const groupLabel = groupLabelOf(spec);

    const column: GridColDef<EmploymentHoursGridRow> = {
      field: spec.id,
      /**
       * The columns panel and the CSV export both read `headerName`, and on their own the labels are
       * ambiguous — a dozen columns are called "Cumulative" or just a year. Qualify them with their
       * group there; the grid header itself keeps the short label, with the group above it.
       */
      headerName: groupLabel ? `${groupLabel}: ${label}` : label,
      renderHeader: () => label,
      // MUI renders `description` as the header tooltip, replacing Tooltip.bind2Control
      ...(spec.tooltipKey && {
        description: resolveTooltip(
          spec.tooltipKey,
          { t, plbName },
          showPlusMinus
        )
      }),
      width: spec.width ?? DEFAULT_COLUMN_WIDTH,
      // Dojo made nothing sortable: rows are the 12 payroll periods, order is meaningful
      sortable: false,
      type: spec.render === 'period' ? 'string' : 'number'
    };

    if (spec.render === 'period') {
      return {
        ...column,
        align: 'center',
        headerAlign: 'center',
        // the row IS one payroll month, so its start date names it
        valueGetter: (_value, row) => row?.valid?.from ?? null,
        valueFormatter: (value: string | null) => formatPeriodMonth(value),
        // the totals row has no `valid.from` to format, so it would otherwise render blank
        renderCell: (params) =>
          params.row?.isTotals
            ? createElement(TotalsLabel, {}, t('employmentHours.total'))
            : params.formattedValue
      };
    }

    return {
      ...column,
      // Only needed where the same data key backs more than one column; MUI requires unique fields.
      ...(spec.id !== spec.source && {
        valueGetter: (_value, row) => row?.[spec.source] ?? null
      }),
      valueFormatter: (value: number | null) =>
        formatHours(shouldFlip && typeof value === 'number' ? -value : value),
      ...(spec.render && {
        renderCell: (params) =>
          createElement(BalanceCell, {
            value: params.value as number | null,
            showPlusMinus,
            isCumulative: spec.render === 'cumulativeBalance',
            wabPercentage: params.row?.wabPercentage,
            wabSettings: params.row?.wabSettings,
            isTotals: params.row?.isTotals
          })
      })
    };
  });

  // Flat, one header per group: the sections only group the row detail panel.
  const columnGroupingModel: GridColumnGroupingModel = HOURS_GROUPS.filter(
    (group) => specs.some((spec) => spec.group === group.id)
  ).map((group) => ({
    groupId: group.id,
    headerName: resolveHeaderLabel(group.label, labelContext),
    children: specs
      .filter((spec) => spec.group === group.id)
      .map((spec) => ({ field: spec.id }))
  }));

  return {
    columns,
    columnGroupingModel,
    availableFields: specs.map((spec) => spec.id)
  };
};
